import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Icon from '@ui/Icon';

import DictionarySuggestionDescription from '@replacements/components/ReplacementsDictionary/DictionarySuggestionDescription';
import DictionarySuggestionList from '@replacements/components/ReplacementsDictionary/DictionarySuggestionList';

import NoResultsFoundV2 from '@replacements/components/ReplacementsDictionary/NoResultsFoundV2';
import NoWordEntered from '@replacements/components/ReplacementsDictionary/NoWordEntered';
import {
  RespellingRegion,
  RespellingRegionEnum,
  SuggestionsByRegion,
} from '@replacements/context/ReplacementsDictionaryContext';
import { useCanGenerateLlmReplacements } from 'abilities/llm';

interface DictionaryBodyProps {
  wordInput?: string;
  displayWordInput?: boolean;
  suggestions: SuggestionsByRegion;
  location: string;
  replacementInput?: string;
}

const useStyles = makeStyles()(theme => ({
  container: {
    flex: 1,
  },
  scrollableContainer: {
    overflowY: 'scroll',
    overflowX: 'hidden',
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.grey[500]} transparent`,
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey[500],
      borderRadius: 6,
    },
    '&::-webkit-scrollbar-corner': {
      display: 'none',
    },
  },
}));

export enum RespellingRegionLabel {
  'English US' = 'English US',
  'English UK' = 'English UK',
}

export const regionLabel = (
  region: RespellingRegion
): RespellingRegionLabel => {
  switch (region) {
    case RespellingRegionEnum.us:
      return RespellingRegionLabel['English US'];
    case RespellingRegionEnum.uk:
      return RespellingRegionLabel['English UK'];

    default:
      return RespellingRegionLabel['English US'];
  }
};

const DictionaryBody = (props: DictionaryBodyProps) => {
  const {
    wordInput,
    displayWordInput = true,
    suggestions,
    location,
    replacementInput,
  } = props;
  const { classes, cx } = useStyles();
  const enableLlmIntegration = useCanGenerateLlmReplacements();
  const suggestionLists = Object.entries(suggestions);
  const noResultsFound = suggestionLists.every(
    ([_, list]) => list.length === 0
  );
  const resultsFound = suggestionLists.some(([_, list]) => list.length > 0);

  const containerRef = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState<boolean | null>(null);

  useEffect(() => {
    if (containerRef && containerRef.current) {
      const element = containerRef.current;
      const canScrollElement = element.scrollHeight > element.clientHeight;
      setCanScroll(canScrollElement);
    }
  }, [canScroll]);

  /**
   * Returns true if scrollbar should be displayed in results list
   * This is to fix UI and remove scrollbar styling which interferes by taking up width
   * As a result, the divider line underneath description is unable to reach the edge of container
   */
  const showScrollbar = useMemo(() => {
    if (canScroll !== null && containerRef.current && resultsFound)
      return canScroll;
    return !noResultsFound;
  }, [canScroll, resultsFound, noResultsFound]);

  if (!wordInput)
    return (
      <Box flex={1}>
        <NoWordEntered />
      </Box>
    );

  return (
    <div
      ref={containerRef}
      className={cx(classes.container, {
        [classes.scrollableContainer]: showScrollbar,
      })}
    >
      <Box>
        {displayWordInput && (
          <DictionarySuggestionDescription wordInput={wordInput} />
        )}
        {noResultsFound && (
          <NoResultsFoundV2
            icon={<Icon type="search" fontSize="large" />}
            subtitle={`We don’t have results for this word yet. Try spelling it another way, or create your own.`}
          />
        )}
      </Box>
      {resultsFound && (
        <>
          {Object.entries(suggestions).map(([region, list], idx, arr) =>
            list.length > 0 ? (
              <React.Fragment key={region}>
                <DictionarySuggestionList
                  title={regionLabel(region as unknown as RespellingRegion)}
                  word={wordInput}
                  suggestions={list}
                  location={location}
                  replacementInput={replacementInput}
                  showBorder={!enableLlmIntegration || idx > 0}
                />
              </React.Fragment>
            ) : null
          )}
        </>
      )}
    </div>
  );
};

export default DictionaryBody;
